import { lazy, Suspense, useEffect } from "react";
import { useRoute, PageHeader } from "@mypos/ui";
import { useAuthStore } from "../lib/authStore";
import { useDrawerStore } from "@till/lib/stores/drawerStore";

// Reuse the Till's Connected wrappers via the @till/pages path alias.
// Each wrapper calls window.till.api which tillShim shims to fetch().
//
// Every page is lazy — Vite splits each into its own chunk so first paint
// only downloads the shell + the page being viewed instead of all 27 pages.
// (Manager-only change: the Electron till loads from local disk, where one
// bundle is fine.)
const lazyPage = <K extends string>(
  load: () => Promise<Record<K, React.ComponentType>>,
  name: K,
) => lazy(() => load().then((m) => ({ default: m[name] })));

const DashboardConnected = lazyPage(() => import("@till/pages/DashboardConnected"), "DashboardConnected");
const SalesHistoryConnected = lazyPage(() => import("@till/pages/SalesHistoryConnected"), "SalesHistoryConnected");
const ConflictsConnected = lazyPage(() => import("@till/pages/ConflictsConnected"), "ConflictsConnected");
const ShiftsDrawersConnected = lazyPage(() => import("@till/pages/ShiftsDrawersConnected"), "ShiftsDrawersConnected");
const ExpensesConnected = lazyPage(() => import("@till/pages/ExpensesConnected"), "ExpensesConnected");
const SyncQueueConnected = lazyPage(() => import("@till/pages/SyncQueueConnected"), "SyncQueueConnected");
const ProductsConnected = lazyPage(() => import("@till/pages/ProductsConnected"), "ProductsConnected");
const BulkImportConnected = lazyPage(() => import("@till/pages/BulkImportConnected"), "BulkImportConnected");
const AddManyProductsConnected = lazyPage(() => import("@till/pages/AddManyProductsConnected"), "AddManyProductsConnected");
const CategoriesBrandsConnected = lazyPage(() => import("@till/pages/CategoriesBrandsConnected"), "CategoriesBrandsConnected");
const PromotionsConnected = lazyPage(() => import("@till/pages/PromotionsConnected"), "PromotionsConnected");
const CustomersConnected = lazyPage(() => import("@till/pages/CustomersConnected"), "CustomersConnected");
const SuppliersConnected = lazyPage(() => import("@till/pages/SuppliersConnected"), "SuppliersConnected");
const StockByStoreConnected = lazyPage(() => import("@till/pages/StockByStoreConnected"), "StockByStoreConnected");
const ReceiveGoodsConnected = lazyPage(() => import("@till/pages/ReceiveGoodsConnected"), "ReceiveGoodsConnected");
const StockMovementsConnected = lazyPage(() => import("@till/pages/StockMovementsConnected"), "StockMovementsConnected");
const TransfersConnected = lazyPage(() => import("@till/pages/TransfersConnected"), "TransfersConnected");
const StockCountConnected = lazyPage(() => import("@till/pages/StockCountConnected"), "StockCountConnected");
const StaffConnected = lazyPage(() => import("@till/pages/StaffConnected"), "StaffConnected");
const StoresAdminConnected = lazyPage(() => import("@till/pages/StoresAdminConnected"), "StoresAdminConnected");
const ReportsConnected = lazyPage(() => import("@till/pages/ReportsConnected"), "ReportsConnected");
const GlobalSettingsConnected = lazyPage(() => import("@till/pages/GlobalSettingsConnected"), "GlobalSettingsConnected");
const AuditLogConnected = lazyPage(() => import("@till/pages/AuditLogConnected"), "AuditLogConnected");
const HeldBillsConnected = lazyPage(() => import("@till/pages/HeldBillsConnected"), "HeldBillsConnected");
const RecentSalesConnected = lazyPage(() => import("@till/pages/RecentSalesConnected"), "RecentSalesConnected");
const ShiftWithDrawer = lazyPage(() => import("@till/pages/ShiftWithDrawer"), "ShiftWithDrawer");
const QuotationsConnected = lazyPage(() => import("@till/pages/QuotationsConnected"), "QuotationsConnected");
const TillSettingsConnected = lazyPage(() => import("@till/pages/TillSettingsConnected"), "TillSettingsConnected");

// PWA-flavoured POS host (camera scan + on-screen receipt).
const PwaPos = lazyPage(() => import("../pages/PwaPos"), "PwaPos");

// Login is the only page the PWA renders through its own auth gate (App.tsx).
// Every other Till page is now reachable in the PWA — hardware-bound actions
// degrade through tillShim's no-op stubs.
const BOUNCED_PAGES = new Set(["login"]);

export function ManagerPagesRouter() {
  const role = useAuthStore((s) => s.user?.role ?? "manager");
  const page = useRoute((s) => s.page);
  const go = useRoute((s) => s.go);
  const drawerId = useDrawerStore((s) => s.activeSessionId);
  const drawerLoading = useDrawerStore((s) => s.loading);
  const hydrateDrawer = useDrawerStore((s) => s.hydrate);

  useEffect(() => {
    if (BOUNCED_PAGES.has(page)) {
      go("dashboard", role);
    }
  }, [page, role, go]);

  // Make sure the PWA knows the current shift status before deciding whether
  // POS is reachable. Without this hydrate, navigating directly to /pos with
  // a shift that the Electron till has just closed leaves drawerId stale at
  // its previous "open" value and the user can keep ringing up sales.
  useEffect(() => {
    void hydrateDrawer();
  }, [hydrateDrawer]);

  // Mirror Till's App.tsx guard: POS and Held Bills require an open drawer.
  // Bouncing to "shift" lands the user on the DrawerOpenWizard so they can
  // open a fresh session.
  useEffect(() => {
    if (drawerLoading) return;
    if (drawerId) return;
    if (page === "pos" || page === "held") {
      go("shift", role);
    }
  }, [page, role, drawerId, drawerLoading, go]);

  if (BOUNCED_PAGES.has(page)) {
    return (
      <PageHeader
        title="Redirecting…"
        subtitle="Loading Dashboard"
      />
    );
  }

  const pageEl = (() => {
    switch (page) {
      case "pos":
        return <PwaPos />;
      case "held":
        return <HeldBillsConnected />;
      case "recent":
        return <RecentSalesConnected />;
      case "shift":
        return <ShiftWithDrawer />;
      case "quotes":
        return <QuotationsConnected />;
      case "till-settings":
        return <TillSettingsConnected />;
      case "dashboard":
        return <DashboardConnected />;
      case "sales":
        return <SalesHistoryConnected />;
      case "conflicts":
        return <ConflictsConnected />;
      case "shifts":
        return <ShiftsDrawersConnected />;
      case "expenses":
        return <ExpensesConnected />;
      case "sync-queue":
        return <SyncQueueConnected />;
      case "products":
        return <ProductsConnected />;
      case "bulk-import":
        return <BulkImportConnected />;
      case "add-many":
        return <AddManyProductsConnected />;
      case "catbrand":
        return <CategoriesBrandsConnected />;
      case "promotions":
        return <PromotionsConnected />;
      case "customers":
        return <CustomersConnected />;
      case "suppliers":
        return <SuppliersConnected />;
      case "stock":
        return <StockByStoreConnected />;
      case "receive":
        return <ReceiveGoodsConnected />;
      case "movements":
        return <StockMovementsConnected />;
      case "transfers":
        return <TransfersConnected />;
      case "count":
        return <StockCountConnected />;
      case "staff":
        return <StaffConnected />;
      case "stores":
        return <StoresAdminConnected />;
      case "reports":
        return <ReportsConnected />;
      case "settings":
        return <GlobalSettingsConnected />;
      case "audit":
        return <AuditLogConnected />;
      default:
        return <PageHeader title="Unknown page" subtitle={`No Manager route for "${page}"`} />;
    }
  })();

  return (
    <Suspense fallback={<PageHeader title="Loading…" subtitle="" />}>
      {pageEl}
    </Suspense>
  );
}
